<script lang="ts" setup>
import { CASE_FILES_OVERVIEW_ENDPOINTS } from '@/core/collections/contracts-api/case-files-overview/case-files-overview.endpoints';
import { CaseFilesOverview } from '@/core/collections/contracts-api/case-files-overview/case-files-overview.interface';
import { CaseFile } from '@/core/collections/contracts-api/employers/case-files/case-file.interface';
import {
   ApiResource,
   CASE_FILE_TYPE_ENDPOINTS,
   CaseFileType,
   EMPLOYER_CASE_FILE_ENDPOINTS,
   EMPLOYER_CASE_FILE_STEP_ENDPOINTS,
   NxtCandidateCaseFileUpdateModal,
   PROGRESSION_STATE_ENDPOINTS,
   ProgressionState,
   ProgressionStateKey,
   useCaseFileSchema,
} from '@nxtpeople/nxt-styles';
import FollowUpFilters from '../components/follow-up-overview/FollowUpFilters.vue';
import FollowUpProgressionState from '../components/follow-up-overview/FollowUpProgressionState.vue';
//#region Globals
const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
//#endregion

//#region Meta
onMounted(() => {
   store.commit('header/SET_BREADCRUMBS', [{ to: '/follow-up', content: t('nxt-follow-up.follow-up') }]);
});
//#endregion

//#region Options
const { data: progressionStates } = useFetch<Array<ProgressionState>>(PROGRESSION_STATE_ENDPOINTS.GET_ALL(), {
   ...cacheFirstConfig,
   transformApiRequest: true,
});

const { data: caseFileTypes } = useFetch<Array<CaseFileType>>(CASE_FILE_TYPE_ENDPOINTS.GET_ALL(), {
   ...cacheFirstConfig,
   transformApiRequest: true,
});
//#endregion

//#region Case Files Overview
const defaultEmployerId = computed(() => store.state.userInfo?.userInfo?.default_employer_id);
const defaultQuery = computed(() => ({
   employer_id: defaultEmployerId.value,
   progression_state_ids: (progressionStates.value ?? [])
      .filter((ps) => [ProgressionStateKey.Open, ProgressionStateKey.InProgress].includes(ps.key))
      .map((ps) => ps.id),
}));

const query = computed({
   get: () => {
      if (Object.keys(route.query).length === 0) return defaultQuery.value;
      return { ...route.query } as any;
   },
   set: (newQuery: any) => router.push({ ...route, query: { ...route.query, ...newQuery } }),
});

const { data, isLoading, execute } = useFetch<Array<CaseFilesOverview>>(CASE_FILES_OVERVIEW_ENDPOINTS.GET_ALL(), {
   immediate: false,
   transformApiRequest: true,
});

function queryChanged(newQuery: Record<string, any>) {
   if (route.name !== 'FollowUpOverview') return;
   execute({ params: { ...newQuery } });
}

function resetFilters() {
   router.push({ ...route, query: { ...defaultQuery.value } });
}

watch(query, queryChanged, { immediate: true, deep: true });
//#endregion

//#region Show Case File
const { CANDIDATE_CASE_FILE_SCHEMA } = useCaseFileSchema();
const caseFileToShow = ref<CaseFile | null>(null);
const savingCaseFile = ref(false);

const currentEmployer = computed(() => (store.state.employer?.employerOptions ?? []).find((e) => e.id == query.value.employer_id));

async function caseFileClicked(caseFile: CaseFile) {
   const employerSyncId = currentEmployer.value?.sync_id;
   if (!employerSyncId) return;

   const { data: caseFileDetails } = await http.get<ApiResource<CaseFile>>(EMPLOYER_CASE_FILE_ENDPOINTS.GET(employerSyncId, caseFile.id));
   caseFileToShow.value = caseFileDetails.data;
}

async function uploadCaseFileStepFile(caseFileId: number, caseFileStepId: number, document: File) {
   if (!currentEmployer.value) return;

   const formData = new FormData();
   formData.append('document', document);

   return await http.post(EMPLOYER_CASE_FILE_STEP_ENDPOINTS.UPLOAD(currentEmployer.value?.sync_id, caseFileId, caseFileStepId), formData);
}

async function saveCaseFile(updatedCaseFile: CaseFile) {
   try {
      savingCaseFile.value = true;

      await Promise.all(
         (updatedCaseFile.steps ?? []).filter((s) => s.file).map((s) => uploadCaseFileStepFile(updatedCaseFile.id, s.id, s.file!)),
      );

      const completedProgressionState = progressionStates.value?.find((ps) => ps.key === ProgressionStateKey.Completed);

      await http.put<ApiResource<CaseFile>>(EMPLOYER_CASE_FILE_ENDPOINTS.PUT(currentEmployer.value?.sync_id, updatedCaseFile.id), {
         ...updatedCaseFile,
         steps: (updatedCaseFile.steps ?? []).map((s) => ({
            ...s,
            file: undefined,
            progression_state_id: s.file ? completedProgressionState?.id : s.progression_state_id,
         })),
      });

      await execute({ params: { ...query.value } });

      caseFileToShow.value = null;
   } finally {
      savingCaseFile.value = false;
   }
}

function closeCaseFile() {
   caseFileToShow.value = null;
}
//#endregion
</script>

<template>
   <div :class="'follow-up-overview'">
      <FollowUpFilters
         v-model:query="query"
         :progression-states="progressionStates ?? []"
         :case-file-types="caseFileTypes ?? []"
         @reset-filters="resetFilters"
      />

      <div class="follow-up-overview__case-files">
         <FollowUpProgressionState
            v-for="caseFilesOverview in data ?? []"
            :key="caseFilesOverview.progression_state_id"
            :case-files="caseFilesOverview.case_files ?? []"
            :progression-state="caseFilesOverview.progression_state"
            @case-file-clicked="caseFileClicked"
         />

         <div class="w-100 d-flex justify-content-center align-items-center" style="min-height: 200px">
            <span v-if="!data?.length && !isLoading">
               {{ $t('generic.feedback.no-results') }}
            </span>
         </div>
      </div>

      <Transition name="fade">
         <NxtGenericFormModal
            v-if="caseFileToShow"
            :modal-type="'custom'"
            :data-type="$t('nxt-candidates.case-files.case-file')"
            :data="caseFileToShow"
            :schema="CANDIDATE_CASE_FILE_SCHEMA"
            :loading="savingCaseFile"
            :custom-title="`${caseFileToShow.case_file_type?.name}: ${caseFileToShow.employee?.candidate?.first_name} ${caseFileToShow.employee?.candidate?.last_name}`"
            @close="closeCaseFile"
            @save="(caseFile) => saveCaseFile(caseFile).then(() => closeCaseFile)"
         >
            <template #default="params">
               <NxtCandidateCaseFileUpdateModal v-bind="params" />
            </template>
         </NxtGenericFormModal>
      </Transition>
   </div>
</template>

<style lang="scss" scoped>
.follow-up-overview {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   height: 100%;
   overflow: hidden;
}

.follow-up-overview__case-files {
   margin-top: var(--nxt-gutter);
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-large);
   overflow: auto;
}
</style>
